/**
 * QuizPage Component
 * Prospect-facing quiz interface with multi-step flow and branching logic
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import AppLayout from '../components/layouts/AppLayout';
import { isEmbedded } from '../utils/authHelper';
import { useEmbedResize, postEmbedComplete } from '../hooks/useEmbedResize';
import QuestionCard from '../components/quiz/QuestionCard';
import QuestionRenderer from '../components/quiz/QuestionRenderer';
import ProgressBar from '../components/quiz/ProgressBar';
import QuizButtons from '../components/quiz/QuizButtons';
import apiClient from '../api/client';
import { QUIZ_ENDPOINTS, ASSESSMENT_ENDPOINTS } from '../api/endpoints';
import { getQuizProspectEmail } from '../utils/navigationState';
import { isQuestionAnswered } from '../utils/questionCompletion';
import { getSubmissionFailure } from '../utils/submissionError';
import { executeRecaptcha, isRecaptchaFailure, quizRequiresRecaptcha } from '../utils/recaptcha';
import type { Quiz, Assessment, ResponseValue } from '../types';
import { useBranching } from '../hooks/useBranching';
import { useAssessmentAutosave } from '../hooks/useAssessmentAutosave';

interface QuizState {
  currentQuestion: number;
  responses: Record<number, ResponseValue>;
  isLoading: boolean;
  error: string | null;
}

function QuizPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { quizId = '1' } = useParams<{ quizId?: string }>();
  const inheritedProspectEmail = getQuizProspectEmail(location.state, location.search);
  useEmbedResize();

  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [state, setState] = useState<QuizState>({
    currentQuestion: 0,
    responses: {},
    isLoading: true,
    error: null,
  });

  const submitting = React.useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Load quiz and initialize assessment
  useEffect(() => {
    let cancelled = false;
    setQuiz(null);
    setState({ currentQuestion: 0, responses: {}, isLoading: true, error: null });
    submitting.current = false;
    setIsSubmitting(false);
    setSubmitError(null);
    const initializeQuiz = async (): Promise<void> => {
      try {
        // Fetch full quiz with questions and answers in one call
        const quizResponse = await apiClient.get<Quiz>(QUIZ_ENDPOINTS.START(quizId));
        // eslint-disable-next-line no-console
        console.log('[CIJ] Quiz start response', {
          quizId,
          hasScoringConfig: Boolean(quizResponse.data.scoringConfig),
          hasCijIntegration: Boolean(quizResponse.data.scoringConfig?.cijIntegration),
          cijEnabled: quizResponse.data.scoringConfig?.cijIntegration?.enabled ?? false,
          fieldMappingCount: quizResponse.data.scoringConfig?.cijIntegration?.fieldMappings?.length ?? 0,
        });
        if (cancelled) return;
        setQuiz(quizResponse.data);

        setState((prev) => ({ ...prev, isLoading: false }));
      } catch (error) {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Failed to load quiz. Please try again.',
        }));
      }
    };

    initializeQuiz();
    return () => { cancelled = true; };
  }, [quizId]);

  const branching = useBranching(quiz?.questions ?? [], quiz?.branchingRules ?? [], state.responses);
  const visibleQuestions = quiz ? branching.getVisibleQuestions(quiz.questions) : [];
  // Answers to skipped questions are kept in state but never saved or scored.
  const activeResponses = useMemo(
    () => filterResponsesByVisibleQuestions(state.responses, branching.visibleQuestionIds),
    [state.responses, branching.visibleQuestionIds],
  );
  const hasVisibleProspectEmailSource = Boolean(quiz?.questions.some((question) =>
    branching.visibleQuestionIds.includes(question.id) &&
    (question.type === 'FormEntry' || question.type === 'CustomerInsightsForm') &&
    question.formFields?.some((field) => field.type === 'email' && field.isProspectEmail),
  ));
  const autosave = useAssessmentAutosave(quizId, quiz, activeResponses, inheritedProspectEmail);

  useEffect(() => {
    setState((prev) => {
      const maxQuestionIndex = Math.max(visibleQuestions.length - 1, 0);
      const currentQuestion = Math.min(prev.currentQuestion, maxQuestionIndex);

      return currentQuestion === prev.currentQuestion
        ? prev
        : { ...prev, currentQuestion };
    });
  }, [visibleQuestions.length]);

  const currentQuestion = visibleQuestions[state.currentQuestion];
  const currentResponse = currentQuestion ? state.responses[currentQuestion.id] : undefined;
  const isAnswered = currentQuestion ? isQuestionAnswered(currentQuestion, currentResponse) : false;
  const numberedQuestions = visibleQuestions.filter((question) => !question.metadata?.hideQuestionNumber);
  const currentQuestionNumber = visibleQuestions
    .slice(0, state.currentQuestion + 1)
    .filter((question) => !question.metadata?.hideQuestionNumber)
    .length;

  React.useEffect(() => {
    if (!import.meta.env.DEV) return;
    // eslint-disable-next-line no-console
    console.warn('[CIJ] Quiz page state', {
      quizId,
      loading: state.isLoading,
      error: state.error,
      currentQuestionId: currentQuestion?.id,
      currentQuestionType: currentQuestion?.type,
      currentQuestionIndex: state.currentQuestion,
      visibleQuestionCount: visibleQuestions.length,
      responseCount: Object.keys(state.responses).length,
      isAnswered,
      isLastQuestion: visibleQuestions.length > 0 && state.currentQuestion >= visibleQuestions.length - 1,
      hasCijIntegration: Boolean(quiz?.scoringConfig?.cijIntegration),
      cijEnabled: quiz?.scoringConfig?.cijIntegration?.enabled ?? false,
    });
  }, [currentQuestion?.id, currentQuestion?.type, isAnswered, quiz?.scoringConfig?.cijIntegration, quizId, state.currentQuestion, state.error, state.isLoading, state.responses, visibleQuestions.length]);

  const handleAnswerChange = (answerId: ResponseValue): void => {
    setState((prev) => ({
      ...prev,
      responses: {
        ...prev.responses,
        [currentQuestion!.id]: answerId,
      },
    }));
  };

  const handlePrevious = (): void => {
    setState((prev) => ({
      ...prev,
      currentQuestion: Math.max(0, prev.currentQuestion - 1),
    }));
  };

  const handleNext = (): void => {
    if (quiz) {
      if (visibleQuestions.length > 0) {
        setState((prev) => ({
        ...prev,
        currentQuestion: Math.min(visibleQuestions.length - 1, prev.currentQuestion + 1),
      }));
    }
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (submitting.current) return;
    submitting.current = true;
    setIsSubmitting(true);
    setSubmitError(null);
    let stage: 'preparing' | 'completion' = 'preparing';
    let sessionToken: string | undefined;
    // eslint-disable-next-line no-console
    console.warn('[CIJ] Quiz submit started', { quizId, responseCount: Object.keys(state.responses).length });

    try {
      const recaptchaRequired = quizRequiresRecaptcha(quiz);
      const { prospectEmail, token, attemptId } = await autosave.prepareCompletion({
        allowAnonymous: !hasVisibleProspectEmailSource,
        reuseAnonymousSession: recaptchaRequired,
      });
      if (!autosave.isActive) return;
      sessionToken = token;
      // eslint-disable-next-line no-console
      console.log('[CIJ] Assessment session created', { quizId });

      const recaptchaToken = recaptchaRequired ? await executeRecaptcha('quiz_submit') : undefined;
      if (!autosave.isActive) return;
      stage = 'completion';
      const response = await apiClient.post<Assessment>(
        ASSESSMENT_ENDPOINTS.CREATE,
        { quizId, prospectEmail, responses: activeResponses, ...(attemptId ? { attemptId } : {}), ...(recaptchaToken ? { recaptchaToken } : {}) },
        { headers: { 'X-Session-Token': token, 'X-Skip-Auth': 'true' } },
      );
      if (!autosave.isActive) return;
      autosave.complete();
      // eslint-disable-next-line no-console
      console.log('[CIJ] Assessment submitted', { assessmentId: response.data.id, outcomeTier: response.data.outcomeTier });

      // Pass assessment data via state so OutcomePage doesn't need to re-fetch
      postEmbedComplete(response.data.id);
      // eslint-disable-next-line no-console
      console.log('[CIJ] Navigating to outcome with scoring config', {
        assessmentId: response.data.id,
        hasScoringConfig: Boolean(quiz?.scoringConfig),
        hasCijIntegration: Boolean(quiz?.scoringConfig?.cijIntegration),
        cijEnabled: quiz?.scoringConfig?.cijIntegration?.enabled ?? false,
        fieldMappingCount: quiz?.scoringConfig?.cijIntegration?.fieldMappings?.length ?? 0,
      });
      navigate(`/outcome/${response.data.id}`, {
        replace: true,
        state: {
          assessment: response.data,
          scoringConfig: quiz?.scoringConfig,
          questions: quiz?.questions,
          quizName: quiz?.name,
          quizDescription: quiz?.description,
          showQuizName: currentQuestion?.metadata?.showQuizName ?? true,
          showQuizDescription: currentQuestion?.metadata?.showQuizDescription ?? true,
        },
      });
    } catch (error) {
      if (!autosave.isActive) return;
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (stage === 'completion' && (status === 401 || status === 403) && !isRecaptchaFailure(error) && sessionToken) {
        autosave.invalidateSessionToken(sessionToken);
      }
      const failure = getSubmissionFailure(error, stage);
      // eslint-disable-next-line no-console
      console.error('[CIJ] Quiz submission failed before outcome navigation', {
        quizId, stage: failure.stage, status: failure.status, code: failure.code, description: failure.description,
      });
      submitting.current = false;
      setIsSubmitting(false);
      setSubmitError(failure.message);
    }
  };

  const embedded = isEmbedded();
  // Avoid defining a component inside render — use a plain function returning JSX to prevent remount
  const wrap = (content: React.ReactNode): JSX.Element =>
    embedded ? <>{content}</> : <AppLayout showNavigation={false}>{content}</AppLayout>;

  if (state.isLoading) {
    return wrap(
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (state.error || !quiz || !currentQuestion) {
    return wrap(
      <Container maxWidth="md">
        <Alert severity="error">{state.error || 'Quiz not found'}</Alert>
      </Container>
    );
  }

  return wrap(
    <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          {submitError && <Alert severity="error">{submitError}</Alert>}
          <Box component="fieldset" disabled={isSubmitting} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
          {(currentQuestion.metadata?.showQuizDescription ?? true) && quiz.description && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" color="textSecondary">{quiz.description}</Typography>
            </Box>
          )}

          {/* Progress Bar - quiz name embedded in header label */}
          <ProgressBar
            currentQuestion={state.currentQuestion}
            totalQuestions={numberedQuestions.length}
            currentQuestionNumber={currentQuestionNumber}
            quizName={(currentQuestion.metadata?.showQuizName ?? true) ? quiz.name : undefined}
            variant="linear"
            showLabel={(currentQuestion.metadata?.showQuizName ?? true) || !currentQuestion.metadata?.hideQuestionNumber}
            showQuestionNumber={!currentQuestion.metadata?.hideQuestionNumber}
            showBar={false}
          />

          {/* Current Question */}
          <QuestionCard
            question={currentQuestion}
            currentQuestionIndex={state.currentQuestion}
            totalQuestions={visibleQuestions.length}
          />

          {/* Question Renderer - Handles all question types */}
          <QuestionRenderer
            question={currentQuestion}
            selectedValue={
              state.responses[currentQuestion.id] !== undefined
                ? state.responses[currentQuestion.id]
                : currentQuestion.type === 'MultiSelect'
                  ? []
                  : currentQuestion.type === 'FormEntry' || currentQuestion.type === 'Dropdown'
                    ? {}
                    : undefined
            }
            onSelect={handleAnswerChange}
            responses={state.responses}
            questions={quiz.questions}
          />

          {/* Navigation Buttons */}
          <Box sx={{ mt: 4 }}>
            <ProgressBar
              currentQuestion={state.currentQuestion}
              totalQuestions={numberedQuestions.length}
              currentQuestionNumber={currentQuestionNumber}
              variant="linear"
              showLabel={false}
            />
          </Box>
          <QuizButtons
            isFirstQuestion={state.currentQuestion === 0}
            isLastQuestion={state.currentQuestion === visibleQuestions.length - 1}
            isLoading={isSubmitting}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onSubmit={handleSubmit}
            disableNext={!isAnswered}
            disableSubmit={!isAnswered}
            submitButtonText={quiz.submitButtonText}
          />
          </Box>
        </Box>
      </Container>
  );
}

export default QuizPage;

function filterResponsesByVisibleQuestions(
  responses: Record<number, ResponseValue>,
  visibleQuestionIds: number[],
): Record<number, ResponseValue> {
  const visible = new Set(visibleQuestionIds.map(String));
  // Preserve UUID keys: numeric conversion collapses them all into "NaN".
  return Object.fromEntries(
    Object.entries(responses).filter(([questionId]) => visible.has(questionId)),
  );
}
